import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { map, noop } from 'underscore';
import { ActionButton, IconButton, Label } from '@fluentui/react';

import { HelpPopup } from '@bingads-webui-react/help-popup';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { AudiencePicker } from './audience-picker';
import { getAudienceSectionStyle } from '../styles/audience-section';

const UnstyledAudiencSection = (props) => {
  const {
    classes,
    i18n,
    title,
    descritpion,
    addLabel,
    addPanelTitle,
    addPanelDescription,
    audienceType,
    removeLeadingSlash,
    onGenericError,
    validationProps,
    updateValidation,
    popupId,
    wizardPerfMarker,
  } = props;

  const perfMarker = useMemo(
    () => {
      const child = wizardPerfMarker.createChild(`Audience_Signal_Add_${validationProps.field}`, { autoEnd: false });
      return child;
    },
    [wizardPerfMarker]
  );

  const [audienceDimensions, setAudienceDimensions] = useState(validationProps.value || []);

  const [showAudiencePicker, setShowAudiencePicker] = useState(false);

  const onAddAudience = useCallback(() => {
    setShowAudiencePicker(true);
  }, []);

  const onAudiencesChanged = useCallback((audiences) => {
    setAudienceDimensions(audiences);
  }, []);

  const onRemoveAudience = useCallback((index) => {
    setAudienceDimensions([
      ...audienceDimensions.slice(0, index),
      ...audienceDimensions.slice(index + 1),
    ]);
  }, [audienceDimensions]);

  useEffect(() => {
    updateValidation([], {
      [validationProps.field]: audienceDimensions,
    });
  }, [audienceDimensions, i18n, updateValidation, validationProps.field]);

  return (
    <div className={classes.sectionContainer}>
      <Label>
        {title}
        {popupId && <HelpPopup
          aria-label={title}
          topic={popupId}
        />}
      </Label>
      <div>{descritpion}</div>
      <div className="audience-dimensions">
        {map(audienceDimensions, ({ Id, Name = '' }, index) => (
          <div className="audience-dimension" key={`audience-${Id}`}>
            <span>{removeLeadingSlash ? Name.replace(/^\//, '') : Name}</span>
            <IconButton
              aria-label={i18n.getString(_TL_('Remove {{Name}}'), { Name })}
              iconProps={{ iconName: 'Cancel' }}
              onClick={() => onRemoveAudience(index)}
            />
          </div>
        ))}
      </div>
      <ActionButton className="btn-add-audiences" iconProps={{ iconName: 'Add' }} onClick={onAddAudience}>{addLabel}</ActionButton>
      {showAudiencePicker && (
        <AudiencePicker
          i18n={i18n}
          isOpen={showAudiencePicker}
          title={addPanelTitle}
          description={addPanelDescription}
          audienceType={audienceType}
          audiences={audienceDimensions}
          onAudiencesChanged={onAudiencesChanged}
          onClose={() => setShowAudiencePicker(false)}
          onGenericError={onGenericError}
          perfMarker={perfMarker}
        />
      )}
    </div>
  );
};

UnstyledAudiencSection.propTypes = {
  classes: PropTypes.shape({
    sectionContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  title: PropTypes.string.isRequired,
  descritpion: PropTypes.string.isRequired,
  addLabel: PropTypes.string.isRequired,
  addPanelTitle: PropTypes.string.isRequired,
  addPanelDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  audienceType: PropTypes.string,
  removeLeadingSlash: PropTypes.bool,
  onGenericError: PropTypes.func,
  validationProps: PropTypes.shape({
    field: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  popupId: PropTypes.string,
  wizardPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
};

UnstyledAudiencSection.defaultProps = {
  audienceType: undefined,
  removeLeadingSlash: false,
  onGenericError: noop,
  popupId: '',
};

export const AudienceSection = withDefaultStyles(withCustomValidation(UnstyledAudiencSection), getAudienceSectionStyle);
